/**
 * Tulsi Offline Sync Engine
 * ==========================
 *
 * Data flow:
 *   User submits form
 *     → apiClient.mutate()
 *         → if online + healthy: POST directly with X-Idempotency-Key
 *             → 2xx: return response, done
 *             → network error: fallthrough to enqueue
 *         → enqueue in outbox with generated idempotency_key
 *     → optimistic UI update (caller uses returned idempotency_key)
 *     → later, drainQueue() picks up:
 *         → POST with X-Idempotency-Key
 *         → 2xx: mark success, GC after 24h
 *         → 409: idempotency hit, mark success (already committed)
 *         → other 4xx: mark failed_final, show in /sync
 *         → 5xx/network: exponential backoff, retry (max 5 attempts)
 *     → sync status pill updates via useSyncStatus hook
 */
import axios from "axios";
import { API_BASE, getToken } from "@/lib/api";
import * as outbox from "./outbox";
import { getBlob, deleteBlob } from "./outboxBlobs";
import { metaSet, metaGet } from "./db";

let _draining = false;
let _timer = null;
const listeners = new Set();

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emit(event) { for (const fn of listeners) { try { fn(event); } catch (_) { /* ignore */ } } }

export function isDraining() { return _draining; }

export async function drainQueue({ manual = false } = {}) {
  if (_draining) return { skipped: true, reason: "already-draining" };
  _draining = true;
  emit({ type: "drain-start", manual });
  const summary = { attempted: 0, success: 0, failed_final: 0, backoff: 0 };
  try {
    const pending = await outbox.listPendingFIFO();
    for (const row of pending) {
      summary.attempted += 1;
      await outbox.markInFlight(row._id);
      emit({ type: "row-start", row });
      try {
        // Phase 23B.5.1 — if the row has a queued blob, upload it FIRST and
        // patch the mutation payload with the returned photo_ref. Failure
        // here re-queues the row without losing the blob.
        const cti = row.body?.client_temp_id;
        if (cti && !row.body?.storefront_photo_ref) {
          const blobRow = await getBlob(cti).catch(() => null);
          if (blobRow?.blob) {
            const fd = new FormData();
            fd.append("file", blobRow.blob, `storefront-${cti}.jpg`);
            fd.append("context", "outlet_storefront");
            const tk = getToken();
            const up = await axios.post(`${API_BASE}/photos/upload`, fd, {
              headers: { ...(tk ? { Authorization: `Bearer ${tk}` } : {}) },
              timeout: 30000,
              validateStatus: () => true,
            });
            if (up.status < 200 || up.status >= 300) {
              // Fail-soft: mark PENDING again so we retry; blob stays.
              await outbox.updateRow(row._id, {
                status: outbox.STATUS.PENDING,
                last_error: `photo upload ${up.status}`,
              });
              summary.backoff += 1;
              emit({ type: "row-photo-retry", row, status: up.status });
              continue;
            }
            const ref = up.data?.ref || up.data?.id || up.data?.photo_ref;
            row.body = { ...row.body,
                          storefront_photo_ref: ref,
                          storefront_photo_meta: { ...(row.body.storefront_photo_meta || {}),
                            uploaded_at_ts: new Date().toISOString() } };
            await outbox.updateRow(row._id, { body: row.body });
          }
        }
        const token = getToken();
        const res = await axios({
          method: row.method,
          url: `${API_BASE}${row.path}`,
          data: row.body,
          headers: {
            ...(row.headers || {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "X-Idempotency-Key": row.idempotency_key,
          },
          timeout: 20000,
          validateStatus: () => true,
        });
        const s = res.status;
        if (s >= 200 && s < 300) {
          await outbox.markSuccess(row._id, res.data);
          // Blob cleanup — success = no orphan blobs.
          if (row.body?.client_temp_id) {
            try { await deleteBlob(row.body.client_temp_id); } catch { /* ignore */ }
          }
          summary.success += 1;
          emit({ type: "row-success", row, response: res.data });
        } else if (s === 409) {
          // Idempotency hit — already committed by an earlier drain.
          await outbox.markSuccess(row._id, res.data);
          if (row.body?.client_temp_id) {
            try { await deleteBlob(row.body.client_temp_id); } catch { /* ignore */ }
          }
          summary.success += 1;
          emit({ type: "row-success", row, response: res.data, idempotent: true });
        } else if (s === 401 || s === 428) {
          // auth expired / password change required → stop draining
          await outbox.updateRow(row._id, { status: outbox.STATUS.PENDING });
          emit({ type: "auth-block", status: s });
          break;
        } else if (s >= 400 && s < 500 && s !== 429) {
          const errText = typeof res.data === "string" ? res.data : (res.data?.error?.message || res.data?.detail || `HTTP ${s}`);
          await outbox.markFailedFinal(row._id, String(errText));
          summary.failed_final += 1;
          emit({ type: "row-failed-final", row, error: errText });
        } else {
          // 5xx or 429 → backoff
          const nextAttempt = (row.attemptCount || 0) + 1;
          if (nextAttempt >= outbox.MAX_ATTEMPTS) {
            await outbox.markFailedFinal(row._id, `HTTP ${s} after ${nextAttempt} attempts`);
            summary.failed_final += 1;
            emit({ type: "row-failed-final", row });
          } else {
            await outbox.markRetry(row._id, `HTTP ${s}`, nextAttempt);
            summary.backoff += 1;
            emit({ type: "row-backoff", row, nextAttempt });
          }
        }
      } catch (err) {
        // Network error
        const nextAttempt = (row.attemptCount || 0) + 1;
        if (nextAttempt >= outbox.MAX_ATTEMPTS) {
          await outbox.markFailedFinal(row._id, `Network: ${err?.message || err}`);
          summary.failed_final += 1;
          emit({ type: "row-failed-final", row });
        } else {
          await outbox.markRetry(row._id, `Network: ${err?.message || err}`, nextAttempt);
          summary.backoff += 1;
          emit({ type: "row-backoff", row, nextAttempt });
        }
      }
    }
    // GC old success rows
    await outbox.gc();
    await metaSet("lastSyncAt", { at: new Date().toISOString(), summary });
  } finally {
    _draining = false;
    emit({ type: "drain-end", summary });
  }
  return summary;
}

export async function lastSyncAt() {
  const m = await metaGet("lastSyncAt");
  return m?.at || null;
}

export function startAutoSync() {
  // Kick on 'online', on interval, on visibility change.
  const kick = () => { drainQueue({ manual: false }).catch(() => {}); };
  window.addEventListener("online", kick);
  window.addEventListener("visibilitychange", () => { if (!document.hidden) kick(); });
  _timer = setInterval(() => { if (!document.hidden) kick(); }, 60_000);   // battery: 1 min, only when visible
  // Also request SW background sync when available.
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    navigator.serviceWorker.ready.then((reg) => {
      reg.sync.register("tulsi-outbox-drain").catch(() => {});
    }).catch(() => {});
    navigator.serviceWorker.addEventListener("message", (evt) => {
      if (evt.data === "tulsi:drain") kick();
    });
  }
  return () => {
    window.removeEventListener("online", kick);
    if (_timer) clearInterval(_timer);
  };
}
